import { useRef, useSyncExternalStore } from 'react';

/**
 * L'estat d'un «Aprova» / «Rebutja» / «Desfés» d'una targeta del xat, fora de React perquè es
 * pugui provar sense DOM i perquè el bloqueig no depengui d'un render.
 *
 * **Un sol vol:** `run()` és síncron fins a marcar `working`, de manera que un doble clic (dos
 * `click` dins del mateix tic, abans que React hagi pogut desactivar el botó) envia **una** sola
 * petició. Un cop resolta bé, la decisió queda presa i cap `run()` més fa res: el botó ja no hi
 * és, però la garantia és aquí i no al marcatge. Si falla, es pot tornar a provar.
 *
 * El servidor té la seva pròpia garantia («el primer guanya»): això només estalvia la segona
 * petició del mateix navegador.
 */
export type DecisionState<TAction extends string, TResult> =
  | { phase: 'idle' }
  | { phase: 'working'; action: TAction }
  | { phase: 'done'; action: TAction; result: TResult }
  | { phase: 'error'; action: TAction; error: string };

export class DecisionController<TAction extends string, TResult> {
  private state: DecisionState<TAction, TResult> = { phase: 'idle' };
  private listeners = new Set<() => void>();

  getState = (): DecisionState<TAction, TResult> => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * Llança l'acció si no n'hi ha cap en curs ni cap de feta. Torna `true` si s'ha enviat.
   * No rebutja mai: l'error queda a l'estat.
   */
  run(action: TAction, send: () => Promise<TResult>): boolean {
    if (this.state.phase === 'working' || this.state.phase === 'done') return false;
    this.set({ phase: 'working', action });
    void (async () => {
      try {
        const result = await send();
        this.set({ phase: 'done', action, result });
      } catch (err) {
        this.set({ phase: 'error', action, error: errorMessage(err) });
      }
    })();
    return true;
  }

  private set(next: DecisionState<TAction, TResult>) {
    this.state = next;
    for (const listener of this.listeners) listener();
  }
}

/** El missatge d'un error de `api.*` (que porta la resposta del backend) o de xarxa. */
export function errorMessage(err: unknown): string {
  const response = (err as { response?: { data?: { message?: unknown } } } | null)?.response;
  const fromApi = response?.data?.message;
  if (typeof fromApi === 'string' && fromApi) return fromApi;
  if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi.join(' · ');
  if (err instanceof Error && err.message) return err.message;
  return 'No s\'ha pogut completar l\'acció';
}

/**
 * Un `DecisionController` per targeta, viu mentre la targeta és muntada. Amb `external`, el que
 * ve de fora (els tests hi posen el seu per pintar la targeta després d'un clic sense DOM).
 */
export function useDecision<TAction extends string, TResult>(external?: DecisionController<TAction, TResult>) {
  const ref = useRef<DecisionController<TAction, TResult> | null>(null);
  if (!ref.current) ref.current = external ?? new DecisionController<TAction, TResult>();
  const controller = ref.current;
  const state = useSyncExternalStore(controller.subscribe, controller.getState, controller.getState);
  return { controller, state };
}
